import { useCallback, useMemo, useState } from 'react'

import { MAX_REPOSITORIES_PER_REQUEST } from '../../utils/api-client/api-schemas.ts'
import type { PickableRepository } from './repository-pick-list.tsx'

/**
 * How many matches one search shows.
 *
 * The search is the API's — both dialogs pass what the reader types as `GET /repositories?name=` —
 * so this bounds a page of matches rather than a window of the estate. An organization with thousands
 * of repositories searches the same way as one with ten, and neither has to be told that some of its
 * repositories were left out of a fetch.
 */
export const SEARCH_PAGE_SIZE = 50

/**
 * How many repositories one create can carry: a project that does not exist yet reports no capacity, so
 * the bound is what one membership request can carry. At the cap the unchecked rows go dead rather than
 * the Confirm button, so the reader is stopped where the overshoot would happen. An add dialog passes
 * the room left in its project instead.
 */
export const SELECTION_CAP = MAX_REPOSITORIES_PER_REQUEST

/**
 * Shown on the unchecked rows once the cap is reached, in terms of the cap actually in force rather
 * than the constant — a dialog that passes a lower one says the number it is enforcing.
 *
 * The add dialog says something different (`roomCapReason`): the design spec gives the two dialogs
 * separate wording, because one is bounded by the flat cap and the other by the room left in an
 * existing project.
 */
export const selectionCapReason = (cap: number) => `Up to ${cap} in a project.`

/**
 * The picked set shared by the create and add dialogs.
 *
 * Selection only. The list itself is the API's answer to the reader's search, so there is nothing to
 * narrow here. The repositories themselves are held, not only their ids: a search can take a picked
 * row off screen, and the dialogs still have to describe what is picked. Membership is manual, so
 * nothing is pre-checked.
 */
export const useRepositoryPicker = ({
  cap = SELECTION_CAP,
}: {
  /** Lower than {@link SELECTION_CAP} when the destination's remaining room is the tighter bound. */
  cap?: number
} = {}) => {
  const [picked, setPicked] = useState<ReadonlyMap<string, PickableRepository>>(new Map())

  // Bounded here rather than in each dialog, so create and add cannot drift apart on the cap.
  // Unchecking always works, even at the cap; only the next check is refused.
  const toggle = useCallback(
    (repository: PickableRepository) => {
      setPicked(current => {
        const next = new Map(current)
        if (next.delete(repository.id)) {
          return next
        }
        if (next.size >= cap) {
          return current
        }
        next.set(repository.id, repository)
        return next
      })
    },
    [cap]
  )

  const clearPicked = useCallback(() => setPicked(new Map()), [])
  const pickedIds = useMemo(() => new Set(picked.keys()), [picked])
  const pickedRepositories = useMemo(() => [...picked.values()], [picked])

  return {
    pickedIds,
    pickedRepositories,
    isAtSelectionCap: picked.size >= cap,
    toggle,
    clearPicked,
  }
}
